import type { UploadFile } from 'antd';
import type { Rule, RuleObject } from 'antd/lib/form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type Validation, imageDraggerDefaultValidations } from './default-validations';

export const useGenerateImageUploadRulesAndValidations = <T>({
  customValidations = [],
}: {
  customValidations?: Validation[];
} = {}): { rules: Rule[]; validations: Validation[] } => {
  const { t } = useTranslation();
  const allValidations = useMemo(() => [...imageDraggerDefaultValidations, ...customValidations], [customValidations]);

  const rules: Rule[] = useMemo(
    () => [
      {
        message: t('Please wait for image upload'),
        validateTrigger: 'onSubmit',

        async validator(_: RuleObject, value?: Array<UploadFile<T>>) {
          for (const file of value ?? []) {
            if (file.status === 'uploading') {
              throw new Error('Value is uploading');
            }
          }

          // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject -- required for async validator
          return Promise.resolve();
        },
      },
      ...allValidations.map(
        (validation): Rule => ({
          message: t(validation.message, { fileName: t('File') }),

          async validator(_, value?: Array<UploadFile<T>>) {
            for (const file of value ?? []) {
              if (file.status === 'error' && file.error instanceof Error && file.error.message === validation.key) {
                throw new Error('error');
              }
            }

            // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject -- required for async validator
            return Promise.resolve();
          },
        }),
      ),
    ],
    [allValidations, t],
  );

  return useMemo(
    () => ({
      rules,
      validations: allValidations,
    }),
    [allValidations, rules],
  );
};
